import type { DeliveryPace, DeliverySeries } from "@aura/shared";
import { css } from "styled-system/css";
import { Columns, LineChart, StackedBars, ed } from "~/components/charts";
import { fmtInt, fmtNum, fmtPct } from "~/lib/format";
import { axisLabel, periodLabel, statsFor } from "./series";


/** Merged PRs per period, split into checked before merge (ink) and not (gray). */
export function MergedColumns({ series, groupKey = "total" }: { series: DeliverySeries; groupKey?: string }) {
  return (
    <Columns
      ariaLabel="Merged PRs per period, split into checked and not checked"
      points={series.periods.map((p) => {
        const st = statsFor(series, p.period, groupKey);
        const merged = st?.merged ?? 0;
        const checked = st?.checked ?? 0;
        return {
          key: p.period,
          value: checked,
          stack: merged - checked,
          label: axisLabel(p, series.grain),
          title: `${periodLabel(p, series.grain)}: ${fmtInt(merged)} merged, ${fmtPct(st?.checkedPct)} checked`,
          // The period in progress keeps its checked split; a note marks it
          // (graying the whole column hid the split).
          ...(p.live && { note: "so far" }),
        };
      })}
      valueLabel="Checked before merge"
      stackLabel="Not checked"
      unit="PRs"
      maxLabels={series.grain === "week" ? 12 : 14}
    />
  );
}

interface LineSeries {
  key: string;
  name: string;
  color: string;
}

/** Recharts line chart with our own key line under it (its legend doesn't fit the page styles). */
function Lines({
  series,
  data,
  height,
  format,
}: {
  series: LineSeries[];
  data: Array<{ date: string; label: string; [k: string]: string | number }>;
  height: number;
  format: (v: number) => string;
}) {
  return (
    <>
      <LineChart
        height={height}
        xAxisKey="label"
        formatDate={(d) => d}
        formatValue={format}
        minTickGap={24}
        showLegend={false}
        series={series}
        data={data}
      />
      <div className={styles.key}>
        {series.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color }} aria-hidden />
            {s.name}
          </span>
        ))}
      </div>
    </>
  );
}

/** Checked and reviewed before merge, as a share of merged PRs. */
export function CheckShareLines({ series }: { series: DeliverySeries }) {
  return (
    <Lines
      height={240}
      format={(v) => `${Math.round(v)}%`}
      series={[
        { key: "checked", name: "Checked before merge", color: ed.emph },
        { key: "reviewed", name: "Reviewed before merge", color: ed.markStrong },
      ]}
      data={series.periods.map((p) => {
        const st = statsFor(series, p.period, "total");
        // A period with no PRs is a gap, not 0%.
        return {
          date: p.period,
          label: axisLabel(p, series.grain),
          ...(st?.checkedPct != null && { checked: st.checkedPct }),
          ...(st?.reviewedPct != null && { reviewed: st.reviewedPct }),
        };
      })}
    />
  );
}

/**
 * How merged PxE PRs were checked, period by period: reviewed, else merged
 * by someone else, else CI passed, else nothing. Each PR counts once.
 */
export function CheckMix({ series, last = 26 }: { series: DeliverySeries; last?: number }) {
  const rows = series.periods.slice(-last).map((p) => {
    const st = statsFor(series, p.period, "total");
    const merged = st?.merged ?? 0;
    const checked = (st?.viaReview ?? 0) + (st?.viaMerger ?? 0) + (st?.viaCi ?? 0);
    return {
      id: p.period,
      label: periodLabel(p, series.grain),
      values: {
        review: st?.viaReview ?? 0,
        merger: st?.viaMerger ?? 0,
        ci: st?.viaCi ?? 0,
        none: merged - checked,
      },
    };
  });
  return (
    <StackedBars
      ariaLabel="How merged PRs were checked before merge, by period"
      mode="share"
      rows={rows}
      keys={[
        { key: "review", label: "Reviewed", color: ed.ink },
        { key: "merger", label: "Merged by someone else", color: ed.markStrong },
        { key: "ci", label: "CI passed only", color: ed.mark },
        { key: "none", label: "Not checked", color: ed.caution },
      ]}
    />
  );
}

/** Median hours from PR opened to first review and to merge. */
export function MedianLines({ series }: { series: DeliverySeries }) {
  return (
    <Lines
      height={240}
      format={(v) => `${fmtNum(v)}h`}
      series={[
        { key: "first", name: "To first review", color: ed.emph },
        { key: "merge", name: "To merge", color: ed.ink },
      ]}
      data={series.periods.map((p) => {
        const st = statsFor(series, p.period, "total");
        return {
          date: p.period,
          label: axisLabel(p, series.grain),
          ...(st?.medianHoursToFirstReview != null && { first: st.medianHoursToFirstReview }),
          ...(st?.medianHoursToMerge != null && { merge: st.medianHoursToMerge }),
        };
      })}
    />
  );
}

const GROUP_COLORS = [ed.emph, ed.ink, ed.markStrong, ed.caution, ed.mark, ed.ink3];

/** Merged PRs per period for the largest groups (products or orgs); the rest add up to Other. */
export function GroupLines({ series, top = 5 }: { series: DeliverySeries; top?: number }) {
  const shown = series.groups.slice(0, top);
  const rest = new Set(series.groups.slice(top).map((g) => g.key));
  const lines = [
    ...shown.map((g, i) => ({ key: g.key, name: g.label, color: GROUP_COLORS[i] ?? ed.mark })),
    ...(rest.size > 0 ? [{ key: "__other", name: "Other", color: ed.mark }] : []),
  ];
  return (
    <Lines
      height={260}
      format={(v) => fmtInt(v)}
      series={lines}
      data={series.periods.map((p) => {
        const row: { date: string; label: string; [k: string]: string | number } = {
          date: p.period,
          label: axisLabel(p, series.grain),
        };
        for (const g of shown) row[g.key] = statsFor(series, p.period, g.key)?.merged ?? 0;
        if (rest.size > 0) {
          row.__other = series.points
            .filter((x) => x.period === p.period && rest.has(x.key))
            .reduce((sum, x) => sum + x.stats.merged, 0);
        }
        return row;
      })}
    />
  );
}

/** Running total of merged PxE PRs by day of quarter, this quarter against the last two. */
export function CumulativeLines({ pace }: { pace: DeliveryPace }) {
  const hasTwo = pace.cumulative.some((c) => c.twoBack !== null);
  return (
    <Lines
      height={280}
      format={(v) => fmtInt(v)}
      series={[
        ...(hasTwo ? [{ key: "twoBack", name: pace.quarterLabels.twoBack, color: ed.mark }] : []),
        { key: "last", name: pace.quarterLabels.last, color: ed.ink },
        // Drawn last so this quarter sits on top.
        { key: "current", name: pace.quarterLabels.current, color: ed.emph },
      ]}
      data={pace.cumulative.map((c) => {
        const row: { date: string; label: string; [k: string]: string | number } = {
          date: String(c.day),
          label: String(c.day),
        };
        if (c.current !== null) row.current = c.current;
        if (c.last !== null) row.last = c.last;
        if (c.twoBack !== null) row.twoBack = c.twoBack;
        return row;
      })}
    />
  );
}

const styles = {
  key: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 16px",
    mt: "6px",
    fontFamily: "sans",
    fontSize: "11.5px",
    color: "ed.ink2",
    "& i": { display: "inline-block", width: "14px", height: "3px", mr: "6px", verticalAlign: "middle" },
  }),
};
